/**
 * Marketing Hub tool registry.
 *
 * Each tool is a real function that queries our Supabase warehouse.
 * Tools are defined without permission checks here; the wizard wraps
 * them with withRBAC() before passing them to AtlasAgent.
 *
 * Tools declared here:
 *  - getCampaignSpendLast7d — 7-day spend by campaign (Google Ads)
 *  - getBookingsByCity       — last 30 days, grouped by city
 *  - getMarketingKpis        — hero KPIs for the current month
 */

import { defineTool, type AgentTool } from '../agent';

type AnyClient = {
  from: (table: string) => any;
  rpc: (name: string, args?: Record<string, unknown>) => any;
};

export function buildMarketingTools(supabase: AnyClient): AgentTool[] {
  const getCampaignSpendLast7d = defineTool<
    { limit?: number },
    Array<{ campaign: string; spend_usd: number; clicks: number; conversions: number }>
  >({
    name: 'getCampaignSpendLast7d',
    description:
      'Return the last 7 days of Google Ads spend grouped by campaign, ordered by spend descending. Use when the user asks "where is my ad budget going" or similar.',
    inputSchema: {
      type: 'object',
      properties: {
        limit: {
          type: 'integer',
          description: 'Top N campaigns to return. Defaults to 10.',
        },
      },
    },
    execute: async (input) => {
      const limit = input.limit ?? 10;
      const { data, error } = await supabase
        .from('mkt_google_ads_daily_view')
        .select('campaign_name, sum_spend_usd, sum_clicks, sum_conversions')
        .gte('date', new Date(Date.now() - 7 * 86400_000).toISOString().slice(0, 10))
        .order('sum_spend_usd', { ascending: false })
        .limit(limit);
      if (error) throw new Error(error.message);
      return (data ?? []).map((r: any) => ({
        campaign: r.campaign_name,
        spend_usd: Number(r.sum_spend_usd ?? 0),
        clicks: Number(r.sum_clicks ?? 0),
        conversions: Number(r.sum_conversions ?? 0),
      }));
    },
  });

  const getBookingsByCity = defineTool<
    { days?: number },
    Array<{ city: string; bookings: number; revenue_aed: number }>
  >({
    name: 'getBookingsByCity',
    description:
      'Group eZhire bookings by pickup city for the last N days (default 30). Use when the user asks about booking concentration, top cities, or geographic performance.',
    inputSchema: {
      type: 'object',
      properties: {
        days: {
          type: 'integer',
          description: 'Lookback window in days. Defaults to 30.',
        },
      },
    },
    execute: async (input) => {
      const days = input.days ?? 30;
      const since = new Date(Date.now() - days * 86400_000).toISOString();
      const { data, error } = await supabase
        .from('mkt_bookings_join')
        .select('pickup_city, revenue_aed')
        .gte('created_at', since);
      if (error) throw new Error(error.message);
      const byCity: Record<string, { bookings: number; revenue_aed: number }> = {};
      for (const row of data ?? []) {
        const city = (row as any).pickup_city ?? 'unknown';
        byCity[city] ??= { bookings: 0, revenue_aed: 0 };
        byCity[city].bookings += 1;
        byCity[city].revenue_aed += Number((row as any).revenue_aed ?? 0);
      }
      return Object.entries(byCity)
        .map(([city, v]) => ({ city, ...v }))
        .sort((a, b) => b.revenue_aed - a.revenue_aed);
    },
  });

  const getMarketingKpis = defineTool<
    Record<string, never>,
    { month: string; spend_usd: number; bookings: number; cpl_usd: number | null }
  >({
    name: 'getMarketingKpis',
    description:
      'Month-to-date marketing KPIs: total spend (USD), bookings, CPL (USD / bookings). Use for "how is marketing doing this month" or top-line summaries.',
    inputSchema: { type: 'object', properties: {} },
    execute: async () => {
      const monthStart = new Date();
      monthStart.setUTCDate(1);
      monthStart.setUTCHours(0, 0, 0, 0);
      const since = monthStart.toISOString();
      const [spendRes, bookRes] = await Promise.all([
        supabase
          .from('mkt_google_ads_daily_view')
          .select('sum_spend_usd')
          .gte('date', since.slice(0, 10)),
        supabase.from('mkt_bookings_join').select('id').gte('created_at', since),
      ]);
      const spend = (spendRes.data ?? []).reduce(
        (s: number, r: any) => s + Number(r.sum_spend_usd ?? 0),
        0,
      );
      const bookings = (bookRes.data ?? []).length;
      return {
        month: since.slice(0, 7),
        spend_usd: Math.round(spend * 100) / 100,
        bookings,
        cpl_usd: bookings > 0 ? Math.round((spend / bookings) * 100) / 100 : null,
      };
    },
  });

  return [getCampaignSpendLast7d, getBookingsByCity, getMarketingKpis] as unknown as AgentTool[];
}

/**
 * Metadata about each Marketing Hub tool — used by the agent wizard
 * to show admins what's available without the tool needing a live
 * Supabase binding. Keep in sync with buildMarketingTools().
 */
export const MARKETING_TOOL_METADATA = [
  {
    name: 'getCampaignSpendLast7d',
    label: 'Campaign spend (last 7d)',
    description: 'Google Ads spend per campaign for the trailing week.',
    requires: ['module:marketing'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getBookingsByCity',
    label: 'Bookings by city',
    description: 'eZhire booking counts + revenue by pickup city for last N days.',
    requires: ['module:marketing'],
    readOrWrite: 'read' as const,
  },
  {
    name: 'getMarketingKpis',
    label: 'Marketing KPIs (MTD)',
    description: 'Month-to-date spend, bookings, CPL.',
    requires: ['module:marketing'],
    readOrWrite: 'read' as const,
  },
];
